import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ChatIcon from '@mui/icons-material/Chat';
import { brandTokens } from '../single-page/theme';
import { OFFICIAL_CONTACTS } from './Footer';
import { useModals } from '../hooks/useModals';

const FAQ_ITEMS = [
  {
    q: 'Quais informações aparecem na consulta completa?',
    a: 'A consulta inclui histórico de roubo e furto, leilão, sinistros, restrições financeiras (gravame), débitos de IPVA/multas e ficha técnica detalhada do veículo, conforme o plano escolhido.',
  },
  {
    q: 'Como recebo o relatório após o pagamento?',
    a: 'O relatório é liberado após a confirmação do pagamento. Você também recebe uma cópia por e-mail conforme o fluxo do produto.',
  },
  {
    q: 'Os dados são atualizados em tempo real?',
    a: 'Consultamos fontes oficiais e parceiros no momento da solicitação, para entregar a informação mais atual disponível.',
  },
  {
    q: 'É seguro informar minha placa no site?',
    a: 'Sim. Utilizamos conexões seguras e tratamos os dados de acordo com a LGPD e as práticas de privacidade do produto.',
  },
];

const Root = styled('section')(({ theme }) => ({
  backgroundColor: brandTokens.navy,
  color: brandTokens.onSurface,
  padding: theme.spacing(10, 3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 2),
  },
}));

const Inner = styled(Box)({
  maxWidth: 960,
  margin: '0 auto',
});

const FaqItem = styled(Accordion)({
  backgroundColor: brandTokens.tonal,
  border: '1px solid rgba(234, 234, 234, 0.1)',
  borderRadius: '12px !important',
  marginBottom: 12,
  boxShadow: 'none',
  color: brandTokens.onSurface,
  '&:before': { display: 'none' },
  '&.Mui-expanded': {
    margin: '0 0 12px',
  },
});

const CtaBanner = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(6),
  padding: theme.spacing(4),
  borderRadius: 16,
  background: brandTokens.signatureGradientBlue,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  alignItems: 'flex-start',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

const FaqSection = () => {
  const [expanded, setExpanded] = useState(null);
  const { setContactOpen } = useModals();
  const whatsapp = OFFICIAL_CONTACTS.whatsapp;

  return (
    <Root>
      <Inner>
        <Typography
          variant="h2"
          sx={{
            textAlign: 'center',
            fontFamily: 'GT Haptik, Raleway, sans-serif',
            fontWeight: 700,
            fontSize: { xs: 28, md: 36 },
            mb: 1.5,
            color: '#fff',
          }}
        >
          Perguntas Frequentes
        </Typography>
        <Typography
          sx={{
            textAlign: 'center',
            fontFamily: 'Roboto, sans-serif',
            fontSize: 16,
            color: brandTokens.onSurfaceVariant,
            maxWidth: 560,
            mx: 'auto',
            mb: 5,
          }}
        >
          Esclareça suas dúvidas sobre nossas consultas e proteja seu patrimônio
          com informações precisas.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.5,
          }}
        >
          {FAQ_ITEMS.map((item) => (
            <FaqItem
              key={item.q}
              expanded={expanded === item.q}
              onChange={(_, isExp) => setExpanded(isExp ? item.q : null)}
              disableGutters
            >
              <AccordionSummary
                expandIcon={<AddIcon sx={{ color: brandTokens.cyan }} />}
                aria-controls={`${item.q}-content`}
                id={`${item.q}-header`}
              >
                <Typography
                  sx={{
                    fontFamily: 'GT Haptik, Roboto, sans-serif',
                    fontWeight: 600,
                    fontSize: 14,
                    pr: 1,
                  }}
                >
                  {item.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  sx={{
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: brandTokens.onSurfaceVariant,
                  }}
                >
                  {item.a}
                </Typography>
              </AccordionDetails>
            </FaqItem>
          ))}
        </Box>

        <CtaBanner>
          <Box>
            <Typography
              sx={{
                fontFamily: 'GT Haptik, Roboto, sans-serif',
                fontWeight: 700,
                fontSize: 20,
                color: brandTokens.navy,
                mb: 0.5,
              }}
            >
              Ainda tem dúvidas?
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: 15,
                color: 'rgba(1, 47, 71, 0.8)',
              }}
            >
              Fale com nossa equipe — estamos prontos para ajudar.
            </Typography>
          </Box>
          {whatsapp ? (
            <Button
              href={`https://wa.me/${whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<ChatIcon />}
              sx={{
                bgcolor: '#25D366',
                color: '#fff',
                fontWeight: 700,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                '&:hover': { bgcolor: '#1ebe57' },
              }}
            >
              Chamar no WhatsApp
            </Button>
          ) : (
            <Button
              onClick={() => setContactOpen(true)}
              startIcon={<ChatIcon />}
              sx={{
                bgcolor: brandTokens.navy,
                color: '#fff',
                fontWeight: 700,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                '&:hover': { bgcolor: brandTokens.navyDark },
              }}
            >
              Fale conosco
            </Button>
          )}
        </CtaBanner>
      </Inner>
    </Root>
  );
};

export default FaqSection;
